/**
 * usePhysicsSimulation — React hook bridging PhysicsEngine with rAF loop
 *
 * Manages a PhysicsEngine instance via ref, runs its own requestAnimationFrame
 * loop, and calls an onFrame callback for imperative DOM updates.
 *
 * Critical perf: positions are NOT managed via React state.
 * The onFrame callback directly sets element.style.transform.
 */

import { useRef, useCallback, useEffect, useState } from 'react';
import { PhysicsEngine } from '@/utils/physics';
import type { PhysicsBody, PhysicsBounds, PhysicsConfig, PhysicsSnapshot } from '@/utils/physics';

export interface UsePhysicsSimulationOptions {
  bounds: PhysicsBounds | null;
  config?: Partial<PhysicsConfig>;
  onFrame?: (snapshot: PhysicsSnapshot) => void;
  onAllSettled?: () => void;
  enabled: boolean;
}

export interface UsePhysicsSimulationResult {
  snapshotRef: React.MutableRefObject<PhysicsSnapshot | null>;
  addBody: (body: PhysicsBody) => void;
  removeBody: (id: string) => void;
  /** Trigger a React re-render (call after removing a body to update DOM tree) */
  forceUpdate: () => void;
  getBodyCount: () => number;
}

export function usePhysicsSimulation(
  options: UsePhysicsSimulationOptions,
): UsePhysicsSimulationResult {
  const { bounds, config, onFrame, onAllSettled, enabled } = options;

  const engineRef = useRef<PhysicsEngine | null>(null);
  const snapshotRef = useRef<PhysicsSnapshot | null>(null);
  const rafRef = useRef<number | null>(null);
  const lastTimeRef = useRef<number>(0);
  const settledFiredRef = useRef(false);
  const [, setTick] = useState(0);

  // Stable callbacks via refs to avoid rAF loop restarts
  const onFrameRef = useRef(onFrame);
  onFrameRef.current = onFrame;
  const onAllSettledRef = useRef(onAllSettled);
  onAllSettledRef.current = onAllSettled;

  // Initialize engine when bounds become available
  useEffect(() => {
    if (!bounds) return;

    if (!engineRef.current) {
      engineRef.current = new PhysicsEngine(bounds, config);
    } else {
      engineRef.current.setBounds(bounds);
    }
  }, [bounds, config]);

  // rAF loop
  useEffect(() => {
    if (!enabled || !bounds || !engineRef.current) return;

    settledFiredRef.current = false;
    lastTimeRef.current = performance.now();

    const loop = (now: number) => {
      const dt = (now - lastTimeRef.current) / 1000;
      lastTimeRef.current = now;

      const engine = engineRef.current;
      if (!engine) return;

      engine.step(dt);
      const snapshot = engine.getSnapshot();
      snapshotRef.current = snapshot;

      onFrameRef.current?.(snapshot);

      if (snapshot.allSettled && !settledFiredRef.current) {
        settledFiredRef.current = true;
        onAllSettledRef.current?.();
      }

      // Keep looping even after settled (items may still be clicked)
      rafRef.current = requestAnimationFrame(loop);
    };

    rafRef.current = requestAnimationFrame(loop);

    return () => {
      if (rafRef.current !== null) {
        cancelAnimationFrame(rafRef.current);
        rafRef.current = null;
      }
    };
  }, [enabled, bounds]);

  const addBody = useCallback((body: PhysicsBody) => {
    engineRef.current?.addBody(body);
  }, []);

  const removeBody = useCallback((id: string) => {
    engineRef.current?.removeBody(id);
  }, []);

  const forceUpdate = useCallback(() => {
    setTick((t) => t + 1);
  }, []);

  const getBodyCount = useCallback(() => {
    return engineRef.current?.getBodyCount() ?? 0;
  }, []);

  // Cleanup engine on unmount
  useEffect(() => {
    return () => {
      engineRef.current?.reset();
      engineRef.current = null;
    };
  }, []);

  return { snapshotRef, addBody, removeBody, forceUpdate, getBodyCount };
}
